import React, { useEffect, useRef, useState } from 'react';
import { SUPPORTED_LOCALES, type Locale } from '@/i18n';

export interface LanguageDropdownProps {
  locale: Locale;
  onChange: (locale: Locale) => void;
  /** Prefix for data-testid attributes (e.g. "terms-locale" -> "terms-locale-trigger", "terms-locale-de"). */
  testIdPrefix?: string;
  /** Horizontal alignment of the popover menu relative to the trigger button. */
  align?: 'left' | 'center' | 'right';
  /** Vertical direction the popover opens. */
  direction?: 'down' | 'up';
  /** Visual variant for integration into navbars vs. cards/modals. */
  variant?: 'default' | 'ghost';
}

export const LanguageDropdown: React.FC<LanguageDropdownProps> = ({
  locale,
  onChange,
  testIdPrefix = 'locale',
  align = 'right',
  direction = 'down',
  variant = 'default',
}) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleOutsideClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  const alignClasses =
    align === 'center'
      ? 'left-1/2 -translate-x-1/2'
      : align === 'left'
        ? 'left-0'
        : 'right-0';

  const directionClasses =
    direction === 'up' ? 'bottom-full mb-1.5' : 'top-full mt-1.5';

  const triggerClasses =
    variant === 'ghost'
      ? 'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium text-text-secondary hover:text-text-primary hover:bg-bg-hover transition-colors cursor-pointer select-none'
      : 'inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium text-text-secondary hover:text-text-primary bg-bg-base/70 hover:bg-bg-hover border border-border-subtle transition-colors cursor-pointer select-none';

  return (
    <div className="relative inline-block text-left" ref={containerRef}>
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Change language"
        data-testid={`${testIdPrefix}-trigger`}
        className={triggerClasses}
      >
        {/* Minimal globe icon */}
        <svg
          width={13}
          height={13}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={1.8}
          strokeLinecap="round"
          strokeLinejoin="round"
          className="text-text-muted shrink-0"
        >
          <circle cx="12" cy="12" r="10" />
          <path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20" />
          <path d="M2 12h20" />
        </svg>
        <span className="font-semibold tracking-wider text-text-primary">
          {locale.toUpperCase()}
        </span>
        <svg
          width={11}
          height={11}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
          className={`text-text-muted transition-transform duration-150 shrink-0 ${
            open ? 'rotate-180' : ''
          }`}
        >
          <path d="m6 9 6 6 6-6" />
        </svg>
      </button>

      {open && (
        <div
          role="listbox"
          className={`absolute ${alignClasses} ${directionClasses} min-w-[152px] p-1 rounded-xl bg-bg-surface border border-border-default shadow-xl shadow-black/50 backdrop-blur-md z-50`}
        >
          {SUPPORTED_LOCALES.map((l) => {
            const isActive = l.code === locale;
            return (
              <button
                key={l.code}
                type="button"
                role="option"
                aria-selected={isActive}
                data-testid={`${testIdPrefix}-${l.code}`}
                onClick={() => {
                  onChange(l.code);
                  setOpen(false);
                }}
                className={`w-full flex items-center justify-between gap-3 px-2.5 py-1.5 rounded-lg text-xs transition-colors cursor-pointer ${
                  isActive
                    ? 'bg-accent/12 text-text-primary font-semibold'
                    : 'text-text-secondary hover:text-text-primary hover:bg-bg-hover font-medium'
                }`}
              >
                <span>{l.native}</span>
                <span className="inline-flex items-center gap-1.5">
                  <span
                    className={`text-[10px] font-mono tracking-wider uppercase ${
                      isActive ? 'text-accent' : 'text-text-muted'
                    }`}
                  >
                    {l.code}
                  </span>
                  {isActive && (
                    <svg
                      width={12}
                      height={12}
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={2.5}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="text-accent shrink-0"
                    >
                      <path d="M20 6 9 17l-5-5" />
                    </svg>
                  )}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};
